import { Platform, ViewStyle } from 'react-native';

export const RTL_LOCALES = ['ar', 'ar_SA', 'ar_EG', 'ar_AE', 'he', 'fa', 'ur'];

/**
 * flexDirection for a row that reads right-to-left when `rtl`. On web an RTL page (`pageRTL`,
 * dir="rtl" from LocaleContext) already mirrors rows, so only content whose direction differs
 * from the page's is reversed; native layout is never mirrored (see nativeLayoutDirection).
 */
export function rowFor(rtl: boolean, pageRTL = rtl): 'row' | 'row-reverse' {
  return rtl !== (Platform.OS === 'web' && pageRTL) ? 'row-reverse' : 'row';
}

export function isRTLLocale(locale: string): boolean {
  if (!locale) return false;
  return RTL_LOCALES.some((rtl) => locale === rtl || locale.startsWith(rtl.split('_')[0]));
}

/**
 * Flips left/right in absolute-positioned styles for RTL.
 * Pass only the directional keys you want flipped.
 */
export function flipStyle(isRTL: boolean, style: ViewStyle): ViewStyle {
  if (!isRTL) return style;
  const flipped: ViewStyle = { ...style };
  if ('left' in style && !('right' in style)) {
    flipped.right = style.left as number;
    delete flipped.left;
  } else if ('right' in style && !('left' in style)) {
    flipped.left = style.right as number;
    delete flipped.right;
  }
  return flipped;
}

/**
 * Returns marginLeft/marginRight flipped to the logical start/end for the current direction.
 */
export function logicalMargin(
  isRTL: boolean,
  options: { start?: number; end?: number }
): { marginLeft?: number; marginRight?: number } {
  const result: { marginLeft?: number; marginRight?: number } = {};
  if (options.start !== undefined) {
    if (isRTL) result.marginRight = options.start;
    else result.marginLeft = options.start;
  }
  if (options.end !== undefined) {
    if (isRTL) result.marginLeft = options.end;
    else result.marginRight = options.end;
  }
  return result;
}

/**
 * Returns paddingLeft/paddingRight flipped to the logical start/end for the current direction.
 */
export function logicalPadding(
  isRTL: boolean,
  options: { start?: number; end?: number }
): { paddingLeft?: number; paddingRight?: number } {
  const result: { paddingLeft?: number; paddingRight?: number } = {};
  if (options.start !== undefined) {
    if (isRTL) result.paddingRight = options.start;
    else result.paddingLeft = options.start;
  }
  if (options.end !== undefined) {
    if (isRTL) result.paddingLeft = options.end;
    else result.paddingRight = options.end;
  }
  return result;
}

const MIRRORED_ICONS: Record<string, string> = {
  'arrow-back': 'arrow-forward',
  'arrow-forward': 'arrow-back',
  'arrow-back-outline': 'arrow-forward-outline',
  'arrow-forward-outline': 'arrow-back-outline',
  'arrow-back-circle': 'arrow-forward-circle',
  'arrow-forward-circle': 'arrow-back-circle',
  'arrow-back-circle-outline': 'arrow-forward-circle-outline',
  'arrow-forward-circle-outline': 'arrow-back-circle-outline',
  'chevron-back': 'chevron-forward',
  'chevron-forward': 'chevron-back',
  'chevron-back-outline': 'chevron-forward-outline',
  'chevron-forward-outline': 'chevron-back-outline',
  'caret-back': 'caret-forward',
  'caret-forward': 'caret-back',
  'caret-back-outline': 'caret-forward-outline',
  'caret-forward-outline': 'caret-back-outline',
};

/**
 * Returns the mirrored icon name for directional icons in RTL.
 * Non-directional icons are returned unchanged.
 */
export function flipIconName(isRTL: boolean, name: string): string {
  if (!isRTL) return name;
  return MIRRORED_ICONS[name] ?? name;
}

/**
 * Returns the physical start edge for the current direction.
 */
export function startEdge(isRTL: boolean): 'left' | 'right' {
  return isRTL ? 'right' : 'left';
}

/**
 * Returns the physical end edge for the current direction.
 */
export function endEdge(isRTL: boolean): 'left' | 'right' {
  return isRTL ? 'left' : 'right';
}

/**
 * Formats a number using Western Arabic digits regardless of locale.
 * Use for prices, counts, phone numbers — never Eastern Arabic digits (١٢٣).
 */
export function formatNumber(
  value: number,
  locale: string,
  options?: Intl.NumberFormatOptions
): string {
  return new Intl.NumberFormat(`${locale}-u-nu-latn`, options).format(value);
}

/**
 * Formats a date using the given locale.
 */
export function formatDate(
  date: Date,
  locale: string,
  options?: Intl.DateTimeFormatOptions
): string {
  return new Intl.DateTimeFormat(locale, options).format(date);
}
